import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { IObjectSecurityModel } from "../../common/models/IObjectSecurityModel";
import { ISortParameters } from "../../common/models/ISortParameters";
import restService from "../rest-service/RestService";
import { IEditRoleDTO, IEditUserDTO, IExportOptions, IRoleAndPermission, ISummaryUserUsageHistoryDTO, IUserNotificationDisableSettings, IUserPermission, IUserSiteAccessedInformation, UserAndRoleAdministrationRoles, UserAndRoleAdministrationUsers } from "./Models";

type OrganizationParams = {
    organizationID: number;
};

type GroupParams = {
    groupID: number;
}

type ExportQueryParams = {
    notificationKey: string;
    mimeType: ApplicationMimeTypesEnum;
    sortParameters: ISortParameters;
    organizationID: number;
}


const UserRoleAdministrationService = {

    //////////////////////////////////////////////////////////////////////////////////////////////
    // #region User Methods

    getUser: async function (
        groupID: number,
        userID: number,
        signal?: AbortSignal
    ): Promise<IEditUserDTO> {

        const path = `groups/${groupID}/userRoleAdministration/users/${userID}`;

        const response = await restService.get<IEditUserDTO>(
            path,
            signal
        );

        return response.data;
    },

    getUsers: async function (
        groupID: number,
        organizationID: number,
        signal?: AbortSignal
    ): Promise<UserAndRoleAdministrationUsers> {

        const path = `groups/${groupID}/userRoleAdministration/users`;

        const response = await restService.get<UserAndRoleAdministrationUsers, OrganizationParams>(
            path,
            signal,
            { organizationID: organizationID }
        );

        return response.data;
    },

    getUserMembership: async function (
        groupID: number,
        userID: number,
        signal?: AbortSignal
    ): Promise<Array<number>> {

        const path = `groups/${groupID}/userRoleAdministration/users/${userID}/membership`;

        const response = await restService.get<Array<number>>(
            path,
            signal
        );

        return response.data;
    },

    getUserPermissions: async function (
        groupID: number,
        organizationID: number,
        userID: number,
        signal?: AbortSignal
    ): Promise<Array<IObjectSecurityModel>> {

        const path = `groups/${groupID}/userRoleAdministration/users/${userID}/permissions`;

        const response = await restService.get<Array<IObjectSecurityModel>, OrganizationParams>(
            path,
            signal,
            { organizationID: organizationID }
        );

        return response.data;
    },

    getEffectiveUserPermissions: async function (
        groupID: number,
        organizationID: number,
        userID: number,
        signal?: AbortSignal
    ): Promise<Array<IObjectSecurityModel>> {

        const path = `groups/${groupID}/userRoleAdministration/users/${userID}/effectivePermissions`;

        const response = await restService.get<Array<IObjectSecurityModel>, OrganizationParams>(
            path,
            signal,
            { organizationID: organizationID }
        );

        return response.data;
    },

    postUser: async function (
        groupID: number,
        organizationID: number,
        userDto: IEditUserDTO,
        signal?: AbortSignal
    ): Promise<IEditUserDTO> {

        const path = `groups/${groupID}/userRoleAdministration/users`;

        const response = await restService.post<IEditUserDTO, IEditUserDTO, OrganizationParams>(
            path,
            userDto,
            signal,
            undefined,
            { organizationID: organizationID }
        );

        return response.data;
    },

    putUser: async function (
        groupID: number,
        organizationID: number,
        userID: number,
        userDto: IEditUserDTO,
        signal?: AbortSignal
    ): Promise<IEditUserDTO> {

        const path = `groups/${groupID}/userRoleAdministration/users/${userID}`;

        const response = await restService
            .put<IEditUserDTO, OrganizationParams, IEditUserDTO>(
                path,
                userDto,
                signal,
                { organizationID: organizationID }
            );

        return response.data;
    },

    deleteUser: async function (
        groupID: number,
        organizationID: number,
        userID: number,
        signal?: AbortSignal
    ): Promise<void> {

        const path = `groups/${groupID}/userRoleAdministration/users/${userID}`;

        const response = await restService.delete<void, OrganizationParams>(
            path,
            signal,
            { organizationID: organizationID }
        );

        return response.data;
    },

    checkUserExists: async function (
        groupID: number,
        userLogin: string,
        signal?: AbortSignal
    ): Promise<boolean> {

        const path = `groups/${groupID}/userRoleAdministration/users/checkExists`;

        const response = await restService.get<boolean, { userLogin: string; }>(path, signal, { userLogin: userLogin });

        return response.data;
    },

    getUserUsageHistory: async function (
        groupID: number,
        userID: number,
        startDate: Date,
        endDate: Date,
        signal?: AbortSignal
    ): Promise<Array<ISummaryUserUsageHistoryDTO>> {

        const path = `groups/${groupID}/userRoleAdministration/users/${userID}/usageHistory`;

        const response = await restService.get<Array<ISummaryUserUsageHistoryDTO>, { startDate: Date, endDate: Date; }>(
            path,
            signal,
            {
                startDate: startDate,
                endDate: endDate
            }
        );

        return response.data;

    },

    createDefaultUser: function (userID: number): IEditUserDTO {
        const user: IEditUserDTO = {
            //organizationID: 1,
            id: userID,
            login: undefined,
            firstName: undefined,
            lastName: undefined,
            companyName: undefined,
            jobTitle: undefined,
            jobFunction: undefined,
            phoneNumber: undefined,
            timeZoneIndex: 20,
            currentPasswordHash: undefined,
            isPasswordChangeRequired: false,
            isDisabled: false,
            isMobileAccessEnabled: false,
            externalSignInName: undefined,
            associatedRoleIds: [],
            recipients: [],
            useLoginForAlarmNotification: false,
            emailNewUserWelcomeMessage: true,
            passwordHash: undefined,
            confirmPasswordHash: undefined,
            lastLoginIPAddress: undefined,
            lastLoginTimestamp: undefined,
            isElynxAdmin: false,
            isLoggedInUser: false,
            multipleCustomers: false,
            isSSO: false,
            mfaEnabled: false,
            mfaSource: undefined,
            uiCustomizationSettingsJSON: undefined,
            uiCustomizationPresetID: undefined
        };

        return user;
    },

    exportUsers: async function (
        groupID: number,
        organizationID: number,
        notificationKey: string,
        mimeType: ApplicationMimeTypesEnum,
        filter?: string,
        sortParams?: ISortParameters,
        signal?: AbortSignal
    ): Promise<string> {

        const path = `groups/${groupID}/userRoleAdministration/users/export`;

        const exportOptions: IExportOptions = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            organizationID: organizationID,
            filter: filter,
            sortParameters: sortParams
        };

        const response = await restService.post<IExportOptions, string>(path, exportOptions, signal);

        return response.data;
    },

    putUserDisable: async function (
        groupID: number,
        organizationID: number,
        userID: number,
        isDisabled: boolean,
        signal?: AbortSignal
    ): Promise<void> {

        const path = `groups/${groupID}/userRoleAdministration/users/${userID}/disable/${isDisabled}`;

        await restService
            .put<void, OrganizationParams, void>(
                path,
                undefined,
                signal,
                { organizationID: organizationID }
            );
    },
    getUserNotificationDisableSettings: async function (
        groupID: number,
        userID: number,
        signal?: AbortSignal
    ): Promise<IUserNotificationDisableSettings> {

        const path = `groups/${groupID}/users/${userID}/notifications/disable-settings`;

        const response = await restService.get<IUserNotificationDisableSettings>(
            path,
            signal
        );

        return response.data;
    },
    saveUserNotificationDisableSettings: async function (
        organizationID: number,
        groupID: number,
        model: IUserNotificationDisableSettings
    ): Promise<IUserNotificationDisableSettings> {

        const path = `organizations/${organizationID}/users/${model.userID}/notifications/disable-settings`;

        const response = await restService.put<IUserNotificationDisableSettings, GroupParams, IUserNotificationDisableSettings>(
            path,
            model,
            undefined,
            {
                groupID
            }
            
        );

        return response.data;
    },
    deleteUserNotificationDisableSettings: async function (
        organizationID: number,
        groupID: number,
        userID: number
    ): Promise<void> {

        const path = `organizations/${organizationID}/users/${userID}/notifications/disable-settings`;

        await restService.delete<undefined, GroupParams>(path, undefined,
            {
                groupID
            }
        );
    },

    getUserPermissionsReport: async function (
        organizationID: number,        
        groupID: number,
        signal?: AbortSignal
    ): Promise<IUserPermission[]> {

        const path = `organizations/${organizationID}/groups/${groupID}/getuserpermissions`;

        const response = await restService.get<IUserPermission[]>(
            path,
            signal
        );

        return response.data;
    },
    exportUserPermissionsReport: async function (
        notificationKey: string,
        organizationID: number,        
        groupID: number,
        mimeType: ApplicationMimeTypesEnum, 
        sortParameters: ISortParameters

    ): Promise<void> {

        const path = `groups/${groupID}/getuserpermissions/export`;

        const queryParams: ExportQueryParams = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            sortParameters: sortParameters,
            organizationID: organizationID
        };


        await restService.post<ExportQueryParams, void>(
            path,
            queryParams
        );
        
    },
    getUserListReport: async function (
        organizationID: number,        
        groupID: number,
        signal?: AbortSignal
    ): Promise<IUserSiteAccessedInformation[]> {

        const path = `v2/organizations/${organizationID}/groups/${groupID}/userlists`;

        const response = await restService.get<IUserSiteAccessedInformation[]>(
            path,
            signal
        );

        return response.data;
    },
    exportUserListReport: async function (
        notificationKey: string,
        organizationID: number,        
        groupID: number,
        mimeType: ApplicationMimeTypesEnum, 
        sortParameters: ISortParameters

    ): Promise<void> {

        const path = `organizations/${organizationID}/groups/${groupID}/userlists/export`;

        const queryParams: ExportQueryParams = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            sortParameters: sortParameters,
            organizationID: organizationID
        };


        await restService.post<ExportQueryParams, void>(
            path,
            queryParams
        );
        
    },    
    resetMfa: async function(groupID: number, userID: number): Promise<void> {
        const path = `groups/${groupID}/userRoleAdministration/users/${userID}/resetMfa`;
        await restService.put<void, void>(path, undefined);
    },


    // #endregion User Methods


    //////////////////////////////////////////////////////////////////////////////////////////////
    // #region Role Methods

    getRole: async function (
        groupID: number,
        roleID: number,
        signal?: AbortSignal
    ): Promise<IEditRoleDTO> {

        const path = `groups/${groupID}/userRoleAdministration/roles/${roleID}`;

        const response = await restService.get<IEditRoleDTO>(
            path,
            signal
        );

        return response.data;
    },

    getRoles: async function (
        groupID: number,
        organizationID: number,
        signal?: AbortSignal
    ): Promise<UserAndRoleAdministrationRoles> {

        const path = `groups/${groupID}/userRoleAdministration/roles`;

        const response = await restService.get<UserAndRoleAdministrationRoles, OrganizationParams>(
            path,
            signal,
            { organizationID: organizationID }
        );

        return response.data;
    },

    getRoleMembership: async function (
        groupID: number,
        roleID: number,
        signal?: AbortSignal
    ): Promise<Array<number>> {

        const path = `groups/${groupID}/userRoleAdministration/roles/${roleID}/membership`;

        const response = await restService.get<Array<number>>(
            path,
            signal
        );

        return response.data;
    },

    getRolePermissions: async function (
        groupID: number,
        organizationID: number,
        roleID: number,
        signal?: AbortSignal
    ): Promise<Array<IObjectSecurityModel>> {

        const path = `groups/${groupID}/userRoleAdministration/roles/${roleID}/permissions`;

        const response = await restService.get<Array<IObjectSecurityModel>, OrganizationParams>(
            path,
            signal,
            { organizationID: organizationID }
        );

        return response.data;
    },

    getEffectiveRolePermissions: async function (
        groupID: number,
        organizationID: number,
        roleIDs: Array<number>,
        signal?: AbortSignal
    ): Promise<Array<IObjectSecurityModel>> {

        const path = `groups/${groupID}/userRoleAdministration/roles/effectivePermissions`;

        const response = await restService.post<Array<number>, Array<IObjectSecurityModel>, OrganizationParams>(
            path,
            roleIDs,
            signal,
            undefined,
            { organizationID: organizationID }
        );

        return response.data;
    },

    postRole: async function (
        groupID: number,
        organizationID: number,
        roleDto: IEditRoleDTO,
        signal?: AbortSignal
    ): Promise<IEditRoleDTO> {

        const path = `groups/${groupID}/userRoleAdministration/roles`;

        const response = await restService.post<IEditRoleDTO, IEditRoleDTO, OrganizationParams>(
            path,
            roleDto,
            signal,
            undefined,
            { organizationID: organizationID }
        );

        return response.data;
    },

    putRole: async function (
        groupID: number,
        organizationID: number,
        roleID: number,
        roleDto: IEditRoleDTO,
        signal?: AbortSignal
    ): Promise<IEditRoleDTO> {

        const path = `groups/${groupID}/userRoleAdministration/roles/${roleID}`;

        const response = await restService
            .put<IEditRoleDTO, OrganizationParams, IEditRoleDTO>(
                path,
                roleDto,
                signal,
                { organizationID: organizationID }
            );

        return response.data;
    },

    deleteRole: async function (
        groupID: number,
        roleID: number,
        signal?: AbortSignal
    ): Promise<void> {

        const path = `groups/${groupID}/userRoleAdministration/roles/${roleID}`;

        const response = await restService.delete<void>(path, signal);

        return response.data;
    },

    // checkRoleExists: async function (
    //     groupID: number,
    //     roleName: string,
    //     signal?: AbortSignal
    // ): Promise<boolean> {

    //     var path = `groups/${groupID}/userRoleAdministration/roles/check`;

    //     const response = await restService.get<boolean, { roleName: string; }>(path, signal, { roleName: roleName });

    //     return response.data;
    // },

    checkCanDeleteRole: async function (
        groupID: number,
        roleID: number,
        signal?: AbortSignal
    ): Promise<boolean> {

        const path = `groups/${groupID}/userRoleAdministration/roles/${roleID}/checkCanDelete`;

        const response = await restService.get<boolean>(path, signal);

        return response.data;
    },

    createDefaultRole: function (roleID: number): IEditRoleDTO {
        const role: IEditRoleDTO = {
            //organizationID: 1,
            id: roleID,
            name: undefined,
            description: undefined,
            externalID: undefined,
            associatedUserIds: new Array<number>(),
            simplePermissions: undefined
        };

        return role;
    },

    exportRoles: async function (
        groupID: number,
        organizationID: number,
        notificationKey: string,
        mimeType: ApplicationMimeTypesEnum,
        filter?: string,
        sortParams?: ISortParameters,
        signal?: AbortSignal
    ): Promise<string> {

        const path = `groups/${groupID}/userRoleAdministration/roles/export`;

        const exportOptions: IExportOptions = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            organizationID: organizationID,
            filter: filter,
            sortParameters: sortParams
        };

        const response = await restService.post<IExportOptions, string>(path, exportOptions, signal);

        return response.data;
    },
    getRolePermissionsReport: async function (
        organizationID: number,        
        groupID: number,
        signal?: AbortSignal
    ): Promise<IRoleAndPermission[]> {

        const path = `organizations/${organizationID}/groups/${groupID}/getrolepermissions`;

        const response = await restService.get<IRoleAndPermission[]>(
            path,
            signal
        );

        return response.data;
    },
    exportRolePermissionsReport: async function (
        notificationKey: string,
        organizationID: number,        
        groupID: number,
        mimeType: ApplicationMimeTypesEnum, 
        sortParameters: ISortParameters

    ): Promise<void> {

        const path = `groups/${groupID}/getrolepermissions/export`;

        const queryParams: ExportQueryParams = {
            notificationKey: notificationKey,
            mimeType: mimeType,
            sortParameters: sortParameters,
            organizationID: organizationID
        };


        await restService.post<ExportQueryParams, void>(
            path,
            queryParams
        );
        
    },


    // #endregion Role Methods
};

export default UserRoleAdministrationService;